import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import "./BusinessHoursForm.css";

const daysOfWeek = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
];

const hourOptions = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));
const minuteOptions = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));
const ampmOptions = ["AM", "PM"];

const InlineBusinessHours = ({ hours, setHours, openPanel, setOpenPanel }) => {
  const [currentDayIndex, setCurrentDayIndex] = useState(0);
  // const [openPanel, setOpenPanel] = useState(null);
  const panelRef = useRef(null);

  const hourRefs = useRef({});
  const minuteRefs = useRef({});
  const ampmRefs = useRef({});

  const day = daysOfWeek[currentDayIndex];
  const currentHours = hours?.[day] ?? { open: "", close: "", closed: false };

  const parseTime = (val) => {
    if (!val) return { hour: "09", minute: "00", ampm: "AM" };
    const [hm, ampm] = val.split(" ");
    const [hour, minute] = hm.split(":");
    return { hour, minute, ampm };
  };

  const formatTime = (hour, minute, ampm) => {
    return `${hour || "09"}:${minute || "00"} ${ampm || "AM"}`;
  };

  const handleTimeChange = (field, part, value) => {
    const parsed = parseTime(currentHours[field] || "09:00 AM");
    const newTime = formatTime(
      part === "hour" ? value : parsed.hour,
      part === "minute" ? value : parsed.minute,
      part === "ampm" ? value : parsed.ampm
    );
    const updated = {
      ...hours,
      [day]: {
        ...hours[day],
        [field]: newTime,
      },
    };
    setHours(updated);
  };

  const togglePanel = (panelName) => {
    setOpenPanel((prev) => (prev === panelName ? null : panelName));
  };

  const toggleClosed = () => {
    setOpenPanel(null);
    const isNowClosed = !currentHours.closed;
    const updated = {
      ...hours,
      [day]: {
        ...hours[day],
        closed: isNowClosed,
        open: isNowClosed ? "" : hours[day]?.open || "09:00 AM",
        close: isNowClosed ? "" : hours[day]?.close || "05:00 PM",
      },
    };
    setHours(updated);
  };

  const prevDay = () => setCurrentDayIndex((prev) => (prev === 0 ? 6 : prev - 1));
  const nextDay = () => setCurrentDayIndex((prev) => (prev === 6 ? 0 : prev + 1));

  useEffect(() => {
    const handleClickOutside = (e) => {
      setTimeout(() => {
        if (panelRef.current && !panelRef.current.contains(e.target)) {
          setOpenPanel(null);
        }
      }, 0);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useLayoutEffect(() => {
    if (!openPanel) return;

    const parsed = parseTime(currentHours[openPanel]);

    const scrollTo = (refMap, value) => {
      const node = refMap?.[openPanel]?.[value];
      if (node && typeof node.scrollIntoView === "function") {
        node.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    };

    const timeout = setTimeout(() => {
      scrollTo(hourRefs.current, parsed.hour);
      scrollTo(minuteRefs.current, parsed.minute);
      scrollTo(ampmRefs.current, parsed.ampm);
    }, 90); // slight delay to allow panel to render

    return () => clearTimeout(timeout); // cleanup on unmount or change
  }, [openPanel, JSON.stringify(currentHours)]);

  const renderPanel = (field) => {
    const parsed = parseTime(currentHours[field]);

    return (
      <div className={`dropdown-panel ${openPanel === field && !currentHours.closed ? "visible" : ""}`}>
        <div className="column">
          <span>Hour</span>
          {hourOptions.map((h) => (
            <button
              type="button"
              key={h}
              ref={(el) => {
                if (!hourRefs.current[field]) hourRefs.current[field] = {};
                hourRefs.current[field][h] = el;
              }}
              className={parsed.hour === h ? "selected" : ""}
              onClick={() => handleTimeChange(field, "hour", h)}
            >
              {h}
            </button>
          ))}
        </div>
        <div className="column">
          <span>Minute</span>
          {minuteOptions.map((m) => (
            <button
              type="button"
              key={m}
              ref={(el) => {
                if (!minuteRefs.current[field]) minuteRefs.current[field] = {};
                minuteRefs.current[field][m] = el;
              }}
              className={parsed.minute === m ? "selected" : ""}
              onClick={() => handleTimeChange(field, "minute", m)}
            >
              {m}
            </button>
          ))}
        </div>
        <div className="column">
          <span>AM/PM</span>
          {ampmOptions.map((a) => (
            <button
              type="button"
              key={a}
              ref={(el) => {
                if (!ampmRefs.current[field]) ampmRefs.current[field] = {};
                ampmRefs.current[field][a] = el;
              }}
              className={parsed.ampm === a ? "selected" : ""}
              onClick={() => handleTimeChange(field, "ampm", a)}
            >
              {a}
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div
      className={`inline-hours-container${openPanel ? " inline-hours-container-open" : ""}`}
    >
      <div className="day-selector">
        <button type="button" onClick={prevDay}>◀</button>
        <span className="current-day">{day}</span>
        <button type="button" onClick={nextDay}>▶</button>
      </div>

      <div className="hours-fields" ref={panelRef}>
        {/* OPEN TIME */}
        <div style={{ position: "relative" }}>
          <div className="hour-title">Open</div>
          <div
            className={`time-display ${currentHours.closed ? "disabled" : ""}`}
            tabIndex={-1}
            onClick={() => !currentHours.closed && togglePanel("open")}
          >
            {currentHours.closed ? "Closed" : currentHours.open || "—"}
          </div>
          {renderPanel("open")}
        </div>

        {/* CLOSE TIME */}
        <div style={{ position: "relative" }}>
          <div className="hour-title">Close</div>
          <div
            className={`time-display ${currentHours.closed ? "disabled" : ""}`}
            tabIndex={-1}
            onClick={() => !currentHours.closed && togglePanel("close")}
          >
            {currentHours.closed ? "Closed" : currentHours.close || "—"}
          </div>
          {renderPanel("close")}
        </div>

        <div className="closed-toggle-inline">
          <input
            type="checkbox"
            checked={currentHours.closed}
            onChange={toggleClosed}
            id="closed-checkbox"
          />
          <label htmlFor="closed-checkbox">Closed for {day}</label>
        </div>
      </div>
    </div>
  );
};

export default InlineBusinessHours;